import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumnGroup,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { memo, useMemo } from "react";
import { wijmoExtendLastColumn } from "src/qubCommon/wijmoHelpers/columnMethods";

const SelectionCheckbox = ({
  checked,
  indeterminate = false,
  disabled = false,
  ariaLabel,
  onChange,
}) => (
  <input
    ref={(element) => {
      if (element) element.indeterminate = indeterminate;
    }}
    type="checkbox"
    checked={checked}
    disabled={disabled}
    aria-label={ariaLabel}
    onMouseDown={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
    onDoubleClick={(event) => event.stopPropagation()}
    onChange={onChange}
    style={{
      display: "block",
      width: 16,
      height: 16,
      margin: "auto",
      cursor: disabled ? "default" : "pointer",
      accentColor: "#003476",
    }}
  />
);

const QubGroupGrid = (props) => {
  const {
    gridColumns,
    gridData,
    initialized,
    editableColumns = [],
    children,
    gridLoadedRows,
    rowSelection,
  } = props;
  const flexGridProps = { ...props };
  delete flexGridProps.rowSelection;
  const filterColumns = (gridColumns ?? [])
    .flatMap((columnGroup) =>
      columnGroup?.children?.length > 0
        ? columnGroup.children.map((child) => child?.fldName)
        : [columnGroup?.fldName],
    )
    .filter(Boolean);
  const selectionColumn = useMemo(
    () =>
      rowSelection?.enabled ? (
        <FlexGridColumnGroup
          binding="__rowSelection"
          header=""
          width={48}
          minWidth={48}
          maxWidth={48}
          align="center"
          isReadOnly
          allowSorting={false}
          allowResizing={false}
        >
          <FlexGridCellTemplate
            cellType="ColumnHeader"
            template={() => {
              const selectableItems = (rowSelection.getVisibleRows?.() ?? []).filter(
                (item) => rowSelection.isRowDisabled?.(item) !== true,
              );
              const selectedCount = selectableItems.filter((item) => {
                const rowId =
                  rowSelection.getRowId?.(item) ?? item?.id ?? item?.Id;
                return rowSelection.selectedIds?.has(rowId);
              }).length;

              return (
                <SelectionCheckbox
                  checked={
                    selectableItems.length > 0 &&
                    selectedCount === selectableItems.length
                  }
                  indeterminate={
                    selectedCount > 0 && selectedCount < selectableItems.length
                  }
                  disabled={
                    selectableItems.length === 0 || rowSelection.disabled
                  }
                  ariaLabel="Επιλογή όλων των ορατών γραμμών"
                  onChange={() => rowSelection.onToggleAll?.(selectableItems)}
                />
              );
            }}
          />
          <FlexGridCellTemplate
            cellType="Cell"
            template={({ item }) => {
              const rowId =
                rowSelection.getRowId?.(item) ?? item?.id ?? item?.Id;

              return (
                <SelectionCheckbox
                  checked={rowSelection.selectedIds?.has(rowId) === true}
                  disabled={rowSelection.isRowDisabled?.(item) === true}
                  ariaLabel="Επιλογή γραμμής"
                  onChange={() => rowSelection.onToggle?.(item)}
                />
              );
            }}
          />
        </FlexGridColumnGroup>
      ) : null,
    [gridData, rowSelection],
  );
  const dataColumns = useMemo(
    () =>
      gridColumns?.length > 0
        ? gridColumns.map((columnGroup, index) => {
            if (columnGroup?.children?.length > 0)
              return (
                <FlexGridColumnGroup
                  key={columnGroup?.grpDescr}
                  header={columnGroup?.grpDescr ?? ""}
                  binding={`group${columnGroup?.grpId}`}
                  align="center"
                >
                  {columnGroup?.children?.map((child) => (
                    <FlexGridColumnGroup
                      key={child?.fldName}
                      isReadOnly={!editableColumns.includes(child.fldName)}
                      header={child?.fldTitle ?? ""}
                      binding={child?.fldName ?? ""}
                      width={
                        Number.isNaN(parseInt(child?.colWidth, 10)) ||
                        child?.colWidth?.includes("*")
                          ? child?.colWidth
                          : parseInt(child?.colWidth, 10)
                      }
                      format={child?.fldFormat}
                      multiLine={child?.multiline}
                      visible={child?.isVisible}
                      align={child?.colAlign ?? "justify"}
                      hideFromPicker={child?.hideFromPicker}
                    >
                      {child.cellTemplates?.map((ct, i) => (
                        <FlexGridCellTemplate
                          key={i}
                          cellType={ct.cellType}
                          template={(ctx) => ct.template(ctx, child)}
                        />
                      ))}
                    </FlexGridColumnGroup>
                  ))}
                </FlexGridColumnGroup>
              );
            return (
              <FlexGridColumnGroup
                key={`${columnGroup?.grpDescr}${index}`}
                header={columnGroup?.fldTitle ?? ""}
                binding={columnGroup?.fldName ?? ""}
                visible={columnGroup?.isVisible}
                align="center"
              />
            );
          })
        : null,
    [editableColumns, gridColumns],
  );
  return (
    <FlexGrid
      allowResizing={3}
      initialized={initialized}
      loadedRows={gridLoadedRows}
      itemsSourceChanged={(flex) =>
        wijmoExtendLastColumn(flex, gridColumns, false, true)
      }
      itemsSource={gridData}
      alternatingRowStep={0}
      {...flexGridProps}
      className="explorer-grid"
    >
      {children}
      {selectionColumn}
      <FlexGridFilter
        filterColumns={filterColumns}
        filterApplied={rowSelection?.onVisibleRowsChange}
      />
      {dataColumns}
    </FlexGrid>
  );
};

const selectionGridPropsAreEqual = (previous, next) => {
  if (!previous.rowSelection?.enabled || !next.rowSelection?.enabled) {
    return false;
  }

  return (
    previous.gridData === next.gridData &&
    previous.gridColumns === next.gridColumns &&
    previous.rowSelection === next.rowSelection &&
    previous.editableColumns === next.editableColumns &&
    previous.stickyHeaders === next.stickyHeaders &&
    previous.headersVisibility === next.headersVisibility &&
    previous.autoRowHeights === next.autoRowHeights &&
    previous.style?.height === next.style?.height &&
    previous.style?.minHeight === next.style?.minHeight
  );
};

export default memo(QubGroupGrid, selectionGridPropsAreEqual);
